import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { delay } from 'rxjs/operators';

import { Theme } from '@shared/services/theme/theme';

import { AuthRequest } from '../../shared/models';
import { ClearAuthRequest, LogInAction, LoginReset } from '../../shared/store/actions/auth.actions';
import { ForgotPasswordReset } from '../../shared/store/actions/forgot-password.action';
import { ForgotUsernameReset } from '../../shared/store/actions/forgot-username.action';
import { BaseForceChangePasswordResponseState } from '../../shared/store/reducers/force-change-password.reducer';
import { BaseForgotUsernameState } from '../../shared/store/reducers/forgot-username.reducer';
import { selectAuthResult } from '../../shared/store/selectors/auth.selector';
import { selectForgotPasswordResult } from '../../shared/store/selectors/forgot-password.selector';
import { selectForgotUsernameResult } from '../../shared/store/selectors/forgot-username.selector';
import { selectForceChangePasswordResult } from '../../shared/store/selectors/user-selector';
import { AppStates } from '../../shared/store/state/app.states';
import { BaseAuthState } from '../../shared/store/state/auth.state';
import { BaseForgotPasswordState } from '../../shared/store/state/forgot-password.state';

@Component({
  selector: 'app-login-right',
  templateUrl: './login-right.component.html',
  styleUrls: ['./login-right.component.scss']
})
export class LoginRightComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  public bsModalRef: BsModalRef;
  public loginForm: UntypedFormGroup;
  public submitted = false;
  public result$: Observable<BaseAuthState>;
  public forgotUsernameResult$: Observable<BaseForgotUsernameState>;
  public forgotPasswordResult$: Observable<BaseForgotPasswordState>;
  public forceChangePasswordResult$: Observable<BaseForceChangePasswordResponseState>;
  public isShowPassword: boolean;
  public forceChangePasswordSuccess: boolean;
  public Theme = Theme;

  private localStore: Observable<any>;

  constructor(
    private readonly store: Store<AppStates>,
    private readonly formBuilder: UntypedFormBuilder
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.store.dispatch(new LoginReset());
    this.resetAlertMessage();
  }

  ngOnInit() {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', [Validators.required]]
    });

    this.localStore = this.store.pipe(untilComponentDestroyed(this));
    this.result$ = this.localStore.pipe(select(selectAuthResult));
    this.forgotUsernameResult$ = this.localStore.pipe(select(selectForgotUsernameResult));
    this.forgotPasswordResult$ = this.localStore.pipe(select(selectForgotPasswordResult));
    this.forceChangePasswordResult$ = this.localStore.pipe(select(selectForceChangePasswordResult));

    this.forceChangePasswordResult$.pipe(delay(300)).subscribe(data => {
      if (data.result.isSuccess) {
        this.store.dispatch(new ClearAuthRequest());
        this.forceChangePasswordSuccess = true;
      }
    });

    this.isShowPassword = false;
  }

  toggleInputType() {
    this.isShowPassword = !this.isShowPassword;
  }

  onSubmit() {
    this.submitted = true;
    this.isShowPassword = false;

    if (this.loginForm.invalid) {
      return;
    }

    const payload: AuthRequest = {
      ...this.loginForm.value,
      grant_type: 'password'
    };

    this.resetAlertMessage();
    this.store.dispatch(new LogInAction(payload));
  }

  get f(): {
    [key: string]: AbstractControl;
  } {
    return this.loginForm.controls;
  }

  resetAlertMessage() {
    this.store.dispatch(new ForgotUsernameReset()); // remove alert success from forgotUsernameResult
    this.store.dispatch(new ForgotPasswordReset()); // remove alert success from forgotPasswordResult
    this.forceChangePasswordSuccess = false;
  }
}
